/**
 * BatchCreationDialog.jsx
 * ======================
 * 
 * A 3-step modal dialog for creating batches when marking orders as "Ready" status.
 * Part of the Order Pipeline feature in the Weekly Planner.
 * 
 * ## Workflow Steps:
 * 1. **Step 1: Batch Entry** - Enter batch codes (min 6 chars), quantities, weights, and cases
 * 2. **Step 2: Use-By Dates** - Assign a use-by date to each product's batches
 * 3. **Step 3: Review** - Review all batch details before final creation
 * 
 * ## Features:
 * - Multi-batch support per product (split quantities across batches)
 * - Existing batch selection dropdown
 * - Real-time quantity validation with percentage indicators
 * - Soft warnings for quantity mismatches (doesn't block submission)
 * - Supplier code and co-branding display for customer requirements
 * 
 * ## Props:
 * @param {boolean} open - Dialog visibility state
 * @param {function} onOpenChange - Handler for dialog state changes
 * @param {object} pendingReadyDispatch - The dispatch being processed
 * @param {array} products - All available products
 * @param {array} batches - All available batches for selection
 * @param {array} cobrandings - Co-branding options for display
 * @param {number} batchStep - Current step (1, 2, or 3)
 * @param {function} setBatchStep - Step state setter
 * @param {object} productBatches - Batch data keyed by product index
 * @param {function} setProductBatches - Batch data setter
 * @param {object} productUseDates - Use-by dates keyed by product index
 * @param {function} setProductUseDates - Use-by date setter
 * @param {array} batchLines - Final generated batch lines for creation
 * @param {function} setBatchLines - Batch lines setter
 * @param {function} getTotalBatchQty - Calculate total quantity for a product
 * @param {function} validateBatchCodes - Validate all batch codes are valid
 * @param {function} validateBatchValues - Validate quantities with warnings
 * @param {function} validateUseDates - Validate all use-by dates are set
 * @param {function} generateFinalBatchLines - Generate final batch data
 * @param {function} handleReadyWithBatch - Submit handler to create batches
 * @param {boolean} creatingBatch - Loading state during creation
 * 
 * @author Cashel Blue Operations Hub Team
 * @lastModified February 21, 2025
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { DatePicker } from '../../components/ui/date-picker';
import { 
  Loader2, 
  Boxes,
  AlertTriangle,
  Trash2,
  Plus,
  CheckCircle2,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';

/**
 * Main BatchCreationDialog Component
 */
export default function BatchCreationDialog({
  open,
  onOpenChange,
  pendingReadyDispatch,
  products,
  batches,
  cobrandings = [], // Added for supplier code display
  // Batch state
  batchStep,
  setBatchStep,
  productBatches,
  setProductBatches,
  productUseDates,
  setProductUseDates,
  productNotes,
  setProductNotes,
  outOfStockProducts,
  setOutOfStockProducts,
  batchLines,
  setBatchLines,
  // Validation functions
  getTotalBatchQty,
  validateBatchCodes,
  validateBatchValues,
  validateUseDates,
  generateFinalBatchLines,
  // Handlers
  handleReadyWithBatch,
  creatingBatch
}) {
  // Warning confirmation state
  const [batchValidationWarnings, setBatchValidationWarnings] = useState([]);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  // No Use By Date tracking per product index
  const [noUseDateProducts, setNoUseDateProducts] = useState({});
  // Track newly added batch for auto-focus
  const [focusTarget, setFocusTarget] = useState(null);
  const focusRef = useRef(null);

  // Prevent mouse wheel from changing number inputs
  const preventScroll = (e) => e.target.blur();

  // Auto-focus on new batch line when added
  useEffect(() => {
    if (focusTarget && focusRef.current) {
      focusRef.current.focus();
      setFocusTarget(null);
    }
  }, [focusTarget, productBatches]);

  // Add a batch to a product
  const addBatchToProduct = (productIndex) => {
    setProductBatches(prev => {
      const newBatches = [...(prev[productIndex] || []), { batch_code: '', quantity: '', weight_kg: '', cases: '' }];
      return { ...prev, [productIndex]: newBatches };
    });
    setFocusTarget({ productIndex, batchIndex: (productBatches[productIndex] || []).length });
  };

  // Remove a batch from a product
  const removeBatchFromProduct = (productIndex, batchIndex) => {
    setProductBatches(prev => {
      const currentBatches = [...(prev[productIndex] || [])];
      if (currentBatches.length > 1) {
        currentBatches.splice(batchIndex, 1);
      }
      return { ...prev, [productIndex]: currentBatches };
    });
  };

  // Update a specific batch for a product
  const updateProductBatch = (productIndex, batchIndex, field, value) => {
    setProductBatches(prev => {
      const currentBatches = [...(prev[productIndex] || [])];
      currentBatches[batchIndex] = { ...currentBatches[batchIndex], [field]: value };
      return { ...prev, [productIndex]: currentBatches };
    });
  };

  // Handle step 1 continue with validation (now goes directly to review)
  const handleStep1Continue = () => {
    // Validate use-by dates are filled
    if (!validateUseDates()) {
      return; // validateUseDates should show errors
    }
    const validation = validateBatchValues();
    if (validation.warnings.length > 0) {
      setBatchValidationWarnings(validation.warnings);
      setShowWarningConfirm(true);
    } else {
      generateFinalBatchLines();
    }
  };

  // Handle dialog close
  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setBatchStep(1);
      setProductBatches({});
      setProductUseDates({});
      setBatchValidationWarnings([]);
      setShowWarningConfirm(false);
      setNoUseDateProducts({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Close button is in DialogContent by default via the X */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5" />
            {batchStep === 1 ? 'Step 1: Batch Codes, Quantities & Use-By Dates' : 
             'Step 2: Review & Create'}
          </DialogTitle>
          <DialogDescription>
            {batchStep === 1 && 'Enter batch codes (min 6 chars), units, weight, and use-by date for each product.'}
            {batchStep === 3 && 'Review batch details before creating dispatch note.'}
          </DialogDescription>
        </DialogHeader>
        
        {pendingReadyDispatch && (() => {
          // Calculate actual batch totals from entered data
          const allBatchEntries = Object.values(productBatches).flat();
          const actualUnits = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);
          const actualCases = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.cases) || 0), 0);
          const actualWeight = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.weight_kg) || 0), 0);
          const hasBatchData = allBatchEntries.length > 0 && (actualUnits > 0 || actualWeight > 0);
          const showBatchTotals = batchStep >= 2 && hasBatchData;
          
          return (
            <div className="bg-muted/30 rounded-lg p-3 mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">
                    <span className="font-medium">{pendingReadyDispatch.customer_name}</span>
                    {pendingReadyDispatch.po_number && <span className="text-muted-foreground"> • PO: {pendingReadyDispatch.po_number}</span>}
                  </p>
                </div>
                {/* Summary - Top RHS: Show batch actuals on step 2+, order data on step 1 */}
                <div className="flex gap-4 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Units' : 'Order Units'}</p>
                    <p className="font-mono font-semibold text-base">
                      {showBatchTotals
                        ? actualUnits.toFixed(0)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0).toFixed(0)
                      }
                    </p>
                  </div>
                  <div className="text-right border-l pl-4">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Cases' : 'Order Cases'}</p>
                    <p className="font-mono font-semibold text-base text-purple-600">
                      {showBatchTotals
                        ? actualCases.toFixed(1)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => {
                            const lineCases = parseFloat(l.cases || l.calculated_cases) || 0;
                            if (lineCases > 0) return sum + lineCases;
                            const product = products.find(p => p.id === l.product_id);
                            const unitsPerCase = product?.units_per_case || 12;
                            return sum + ((parseFloat(l.quantity) || 0) / unitsPerCase);
                          }, 0).toFixed(1)
                      }
                    </p>
                  </div>
                  <div className="text-right border-l pl-4">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Weight (kg)' : 'Order Weight (kg)'}</p>
                    <p className="font-mono font-semibold text-base text-blue-600">
                      {showBatchTotals
                        ? actualWeight.toFixed(2)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => {
                            const lineWeight = parseFloat(l.estimated_weight || l.weight_override || l.total_weight) || 0;
                            if (lineWeight > 0) return sum + lineWeight;
                            const product = products.find(p => p.id === l.product_id);
                            const weightPerUnit = product?.weight_per_unit || 0;
                            return sum + ((parseFloat(l.quantity) || 0) * weightPerUnit);
                          }, 0).toFixed(2)
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 py-2">
          {[1, 2].map(step => {
            const isActive = (step === 1 && batchStep === 1) || (step === 2 && batchStep === 3);
            const isPast = (step === 1 && batchStep === 3);
            return (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isActive || isPast ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {step}
                </div>
                {step < 2 && <div className={`w-12 h-1 mx-1 ${isPast ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            );
          })}
        </div>
        
        {/* Step 1: Batch Codes & Quantities */}
        {batchStep === 1 && (
          <div className="space-y-4 py-4">
            {(pendingReadyDispatch?.lines || []).map((line, productIndex) => {
              const product = products.find(p => p.id === line.product_id);
              const isProductMissing = !product && line.product_id;
              const currentBatches = productBatches[productIndex] || [];
              const totalQty = getTotalBatchQty(productIndex);
              const orderedQty = parseFloat(line.quantity) || 0;
              const orderedWeightFromLine = parseFloat(line.estimated_weight || line.weight_override || line.total_weight) || 0;
              const orderedWeight = orderedWeightFromLine > 0 ? orderedWeightFromLine : (orderedQty * (product?.weight_per_unit || 0));
              const orderedCasesFromLine = parseFloat(line.cases || line.calculated_cases) || 0;
              const orderedCases = orderedCasesFromLine > 0 ? orderedCasesFromLine : (orderedQty / (product?.units_per_case || 12));
              const totalWeight = currentBatches.reduce((sum, b) => sum + (parseFloat(b.weight_kg) || 0), 0);
              
              return (
                <div key={productIndex} className={`border rounded-lg p-4 ${outOfStockProducts[productIndex] ? 'bg-red-50/50 border-red-200' : isProductMissing ? 'bg-red-50 border-red-300' : 'bg-white'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">
                        {line.product_name || product?.name || 'Product'}
                        {/* Product status warning */}
                        {isProductMissing && (
                          <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded">NOT FOUND</span>
                        )}
                        {outOfStockProducts[productIndex] && (
                          <span className="ml-2 px-2.5 py-0.5 bg-red-600 text-white text-xs font-bold rounded tracking-wide">OUT OF STOCK</span>
                        )}
                        {/* Show Customer Code and Brand/Packaging in red */}
                        {(line.supplier_product_code || line.cobranding_id) && (
                          <span className="ml-2 text-red-600 font-normal text-sm">
                            {line.supplier_product_code && (
                              <span className="mr-2">[{line.supplier_product_code}]</span>
                            )}
                            {line.cobranding_id && cobrandings.find(c => c.id === line.cobranding_id) && (
                              <span>({cobrandings.find(c => c.id === line.cobranding_id)?.name})</span>
                            )}
                          </span>
                        )}
                      </p>
                      {isProductMissing && (
                        <p className="text-red-600 text-sm mt-1">
                          This product was not found. It may have been deleted.
                        </p>
                      )}
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span>Ordered: <span className="font-mono font-medium">{orderedQty}</span> units</span>
                        {(line.cases || line.calculated_cases) > 0 && (
                          <span className="text-purple-600 font-medium">| Cases: <span className="font-mono">{parseFloat(line.cases || line.calculated_cases || 0).toFixed(1)}</span></span>
                        )}
                        {orderedWeight > 0 && (
                          <span className="text-blue-600 font-medium">| Weight: <span className="font-mono">{orderedWeight.toFixed(2)}</span> kg</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <span>Variations will show as warnings</span>
                      </div>
                      {line.notes && (
                        <div className="mt-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                          <span className="font-medium text-xs text-blue-600 mr-1">Note:</span>{line.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {outOfStockProducts[productIndex] ? (
                        <div className="text-sm font-bold px-4 py-2 rounded bg-red-100 text-red-700">
                          Line will be excluded from totals
                        </div>
                      ) : (<>
                      {(() => {
                        const qtyPercent = orderedQty > 0 ? ((totalQty - orderedQty) / orderedQty) * 100 : 0;
                        const hasVariation = Math.abs(qtyPercent) > 0.1;
                        const qtyClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        const displayQty = Number.isInteger(totalQty) ? totalQty : totalQty.toFixed(2);
                        const displayOrdered = Number.isInteger(orderedQty) ? orderedQty : orderedQty.toFixed(2);
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${qtyClass}`}>
                            Units: {displayQty} / {displayOrdered} {hasVariation && `(${qtyPercent > 0 ? '+' : ''}${qtyPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      {orderedWeight > 0 && (() => {
                        const weightPercent = orderedWeight > 0 ? ((totalWeight - orderedWeight) / orderedWeight) * 100 : 0;
                        const hasVariation = Math.abs(weightPercent) > 0.1;
                        const weightClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${weightClass}`}>
                            Wt: {totalWeight.toFixed(2)} / {orderedWeight.toFixed(2)} kg {hasVariation && `(${weightPercent > 0 ? '+' : ''}${weightPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      {orderedCases > 0 && (() => {
                        const totalCases = currentBatches.reduce((sum, b) => sum + (parseFloat(b.cases) || 0), 0);
                        const casesPercent = orderedCases > 0 ? ((totalCases - orderedCases) / orderedCases) * 100 : 0;
                        const hasVariation = Math.abs(casesPercent) > 0.1;
                        const casesClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${casesClass}`}>
                            Cases: {totalCases.toFixed(1)} / {orderedCases.toFixed(1)} {hasVariation && `(${casesPercent > 0 ? '+' : ''}${casesPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      </>)}
                    </div>
                  </div>
                  
                  {outOfStockProducts[productIndex] ? (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-center">
                      <p className="text-sm text-red-700 font-medium">This product will appear on the dispatch note as OUT OF STOCK</p>
                    </div>
                  ) : (
                  <div className="space-y-2">
                    {currentBatches.map((batch, batchIndex) => (
                      <div key={batchIndex} className="space-y-1">
                        <div className="flex items-center gap-3 p-2 bg-muted/30 rounded">
                          <div className="flex-1">
                            <Label className="text-xs">Batch Code (min 6 chars) *</Label>
                            <Input
                              ref={focusTarget?.productIndex === productIndex && focusTarget?.batchIndex === batchIndex ? focusRef : undefined}
                              value={batch.batch_code}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'batch_code', e.target.value)}
                              placeholder="Enter 6+ char batch code"
                              className={`font-mono text-sm h-9 ${batch.batch_code.length >= 6 ? 'border-green-500' : batch.batch_code.length > 0 ? 'border-red-500' : ''}`}
                              data-testid={`batch-code-${productIndex}-${batchIndex}`}
                            />
                            {batch.batch_code.length > 0 && batch.batch_code.length < 6 && (
                              <span className="text-xs text-red-500">{6 - batch.batch_code.length} more chars needed</span>
                            )}
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">Units *</Label>
                            <Input
                              type="number"
                              value={batch.quantity}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'quantity', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="Qty"
                              className="font-mono text-sm h-9"
                              data-testid={`batch-qty-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          <div className="w-28">
                            <Label className="text-xs text-blue-600 font-medium">Weight (kg) *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={batch.weight_kg}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'weight_kg', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="0.00"
                              className="font-mono text-sm h-9 border-blue-300 focus:border-blue-500"
                              data-testid={`batch-weight-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">Cases</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={batch.cases || ''}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'cases', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="0.0"
                              className="font-mono text-sm h-9"
                              data-testid={`batch-cases-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          {currentBatches.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 mt-5"
                              onClick={() => removeBatchFromProduct(productIndex, batchIndex)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {/* Per-batch use-by date override (only when multiple batches) */}
                        {currentBatches.length > 1 && (
                          <div className="flex items-center gap-3 pl-2 pb-1">
                            <Checkbox
                              id={`diff-date-${productIndex}-${batchIndex}`}
                              checked={batch.has_own_date || false}
                              onCheckedChange={(checked) => {
                                updateProductBatch(productIndex, batchIndex, 'has_own_date', checked);
                                if (!checked) {
                                  updateProductBatch(productIndex, batchIndex, 'own_use_by_date', '');
                                  updateProductBatch(productIndex, batchIndex, 'own_no_date', false);
                                }
                              }}
                              className="h-3.5 w-3.5"
                              data-testid={`diff-date-${productIndex}-${batchIndex}`}
                            />
                            <label htmlFor={`diff-date-${productIndex}-${batchIndex}`} className="text-[11px] text-muted-foreground cursor-pointer">
                              Different use-by date for this batch
                            </label>
                            {batch.has_own_date && (
                              <div className="flex items-center gap-2 ml-2">
                                {batch.own_no_date ? (
                                  <div className="h-8 flex items-center px-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 font-medium">No Date</div>
                                ) : (
                                  <div className="w-40">
                                    <DatePicker
                                      value={batch.own_use_by_date || ''}
                                      onChange={(d) => updateProductBatch(productIndex, batchIndex, 'own_use_by_date', d)}
                                      placeholder="Select date"
                                      data-testid={`batch-own-date-${productIndex}-${batchIndex}`}
                                    />
                                  </div>
                                )}
                                <Checkbox
                                  id={`batch-no-date-${productIndex}-${batchIndex}`}
                                  checked={batch.own_no_date || false}
                                  onCheckedChange={(checked) => {
                                    updateProductBatch(productIndex, batchIndex, 'own_no_date', checked);
                                    if (checked) updateProductBatch(productIndex, batchIndex, 'own_use_by_date', 'No Date');
                                    else updateProductBatch(productIndex, batchIndex, 'own_use_by_date', '');
                                  }}
                                  className="h-3 w-3"
                                  data-testid={`batch-own-nodate-${productIndex}-${batchIndex}`}
                                />
                                <label htmlFor={`batch-no-date-${productIndex}-${batchIndex}`} className="text-[10px] text-muted-foreground cursor-pointer">No date</label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addBatchToProduct(productIndex)}
                      disabled={outOfStockProducts[productIndex]}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Another Batch
                    </Button>

                    {/* Use-By Date — inline next to Add Another Batch */}
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="w-48">
                        <Label className={`text-xs font-medium ${!noUseDateProducts[productIndex] && !productUseDates[productIndex] ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                          Use-By Date {!noUseDateProducts[productIndex] && <span className="text-red-500">*</span>}
                        </Label>
                        {noUseDateProducts[productIndex] ? (
                          <div className="h-9 flex items-center px-3 bg-amber-50 border border-amber-200 rounded-md">
                            <span className="text-xs font-medium text-amber-700">No Date</span>
                          </div>
                        ) : (
                          <DatePicker
                            value={productUseDates[productIndex] || ''}
                            onChange={(formattedDate) => setProductUseDates(prev => ({ ...prev, [productIndex]: formattedDate }))}
                            placeholder="Select date"
                            className={!productUseDates[productIndex] ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : ""}
                            data-testid={`use-by-date-${productIndex}`}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-4">
                        <Checkbox
                          id={`no-date-${productIndex}`}
                          checked={noUseDateProducts[productIndex] || false}
                          onCheckedChange={(checked) => {
                            setNoUseDateProducts(prev => ({ ...prev, [productIndex]: checked }));
                            if (checked) {
                              setProductUseDates(prev => ({ ...prev, [productIndex]: 'No Date' }));
                            } else {
                              setProductUseDates(prev => ({ ...prev, [productIndex]: '' }));
                            }
                          }}
                          className="h-3.5 w-3.5"
                          data-testid={`no-date-checkbox-${productIndex}`}
                        />
                        <label htmlFor={`no-date-${productIndex}`} className="text-[11px] text-muted-foreground cursor-pointer">
                          No Date
                        </label>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2">
                        <Checkbox
                          id={`out-of-stock-${productIndex}`}
                          checked={outOfStockProducts[productIndex] || false}
                          onCheckedChange={(checked) => {
                            setOutOfStockProducts(prev => ({ ...prev, [productIndex]: checked }));
                          }}
                          className="h-3.5 w-3.5 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                          data-testid={`out-of-stock-checkbox-${productIndex}`}
                        />
                        <label htmlFor={`out-of-stock-${productIndex}`} className="text-[11px] text-red-600 font-semibold cursor-pointer">
                          Out Of Stock
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="flex justify-between gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStep1Continue} 
                disabled={!validateBatchCodes()}
                data-testid="btn-step1-continue"
              >
                Review & Create
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Warning Confirmation Dialog */}
            <Dialog open={showWarningConfirm} onOpenChange={setShowWarningConfirm}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-5 h-5" />
                    Confirm Values
                  </DialogTitle>
                  <DialogDescription>
                    The following values differ from the original order. Please confirm to continue.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-4">
                  {batchValidationWarnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-amber-50 rounded border border-amber-200">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <span className="text-amber-800">{warning}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowWarningConfirm(false)}>
                    Go Back
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowWarningConfirm(false);
                      generateFinalBatchLines();
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Continue Anyway
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
        
        {/* Step 2: Review & Create (was Step 3) */}
        {batchStep === 3 && (
          <>
            <div className="space-y-4 py-2">
              {/* Professional Review Header */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Batch Creation Summary</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Please review the batch details below. Upon confirmation, these batches will be created and the dispatch note will reflect all quantities and weights as displayed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Totals */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold font-mono">{batchLines.filter(l => !l.out_of_stock).length}</p>
                  <p className="text-xs text-muted-foreground">Batch{batchLines.filter(l => !l.out_of_stock).length !== 1 ? 'es' : ''} to Create</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold font-mono">
                    {batchLines.filter(l => !l.out_of_stock).reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0).toFixed(0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Units</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                  <p className="text-2xl font-bold font-mono text-blue-700">
                    {batchLines.filter(l => !l.out_of_stock).reduce((sum, l) => sum + (parseFloat(l.weight_kg) || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-blue-600 font-medium">Total Weight (kg)</p>
                </div>
              </div>

              {/* Out of stock notice */}
              {batchLines.some(l => l.out_of_stock) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <p className="text-sm text-red-700">
                    <span className="font-bold">{batchLines.filter(l => l.out_of_stock).length} product(s) marked OUT OF STOCK</span> — excluded from totals, will appear on dispatch note.
                  </p>
                </div>
              )}

              {/* Batch Details Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-medium">Product</th>
                      <th className="text-left p-3 font-medium">Batch Code</th>
                      <th className="text-right p-3 font-medium">Units</th>
                      <th className="text-right p-3 font-medium">Weight (kg)</th>
                      <th className="text-right p-3 font-medium">Cases</th>
                      <th className="text-left p-3 font-medium">Use-By Date</th>
                      <th className="text-left p-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {batchLines.map((line, index) => (
                      <tr key={index} className={line.out_of_stock ? 'bg-red-50' : 'hover:bg-muted/30'}>
                        <td className="p-3">
                          <span className="font-medium">{line.product_name}</span>
                          {line.out_of_stock && (
                            <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded tracking-wider">OUT OF STOCK</span>
                          )}
                        </td>
                        <td className="p-3 font-mono text-sm">{line.out_of_stock ? '—' : line.batch_code}</td>
                        <td className="p-3 text-right font-mono">{line.out_of_stock ? '—' : line.quantity}</td>
                        <td className="p-3 text-right font-mono text-blue-600 font-medium">{line.out_of_stock ? '—' : parseFloat(line.weight_kg || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono">{line.out_of_stock ? '—' : parseFloat(line.cases || 0).toFixed(1)}</td>
                        <td className={`p-3 font-mono text-sm ${line.expiry_date === 'No Date' ? 'text-amber-600 italic' : ''}`}>
                          {line.out_of_stock ? '—' : line.expiry_date === 'No Date' ? 'No Date' : line.expiry_date}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground truncate max-w-[150px]" title={line.notes || ''}>
                          {line.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Confirmation Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Once created, batch codes cannot be changed. The dispatch note will be updated with these batch details and locked for dispatch.
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex justify-between sm:justify-between border-t pt-4">
              <Button variant="outline" onClick={() => setBatchStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Edit
              </Button>
              <Button 
                onClick={handleReadyWithBatch} 
                disabled={creatingBatch} 
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-create-batch-ready"
              >
                {creatingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirm & Create {batchLines.length} Batch{batchLines.length !== 1 ? 'es' : ''}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
